/**
 * GroupedRows — rows of a `<table>` under header rows, one per group (grant
 * lists redesign 2026-09-19, R1–R2; guideline §3.2).
 *
 * A headless helper: the page decides the key (`groupKey`), the fixed order
 * of groups and their labels; this component draws, for each non-empty group
 * in that order, a `<tr data-testid="group-row">` holding one `colSpan` cell
 * with a chevron button (aria-expanded), the label, and — only when the group
 * is collapsed — a mono caption "· N hidden" (§6.2: a folded thing may say
 * how much it hides; an open header carries no count, §1.6). Collapse state
 * is component state only; a reload reopens every group. With `enabled`
 * false the rows render flat, in the order given, and no header prints.
 *
 * Rows whose key is not in `order` land at the end under their own label
 * (the key itself when `label` has none), so a record is never dropped.
 *
 * A SECOND level is optional (families one list, 2026-09-20): supply
 * `subGroupKey` with its own `subOrder`/`subLabel` and each outer group is
 * bucketed again under indented headers (Families: cohort year, then cycle).
 * Leave them out and the component behaves exactly as it did — which is what
 * keeps the five single-level callers (Applications, Requests, Awards,
 * Resources, Users) untouched by this change.
 *
 * Both levels obey the same two rules: an OPEN header carries no count
 * (§1.6), a COLLAPSED one says "· N hidden" (§6.2). The one exception is
 * `countOpen` (Families grouped by TRFer, 2026-10-24): there the count IS the
 * reading — how many families each person holds — so an open header prints
 * "· N" too. `countInLabel` (Applications, Victor 2026-10-31: "To read (n)")
 * puts the count in the header's own words, open or folded, in place of the
 * "· N hidden" caption; `countMore` adds "+" when more rows may still page in.
 * Collapsing an outer
 * group takes its inner headers down with it — the fold hides a section of
 * the list, not just its rows.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { MonoLabel } from './MonoLabel';
import { cn } from '@/lib/utils';

export interface GroupedRowsProps<Row> {
  rows: Row[];
  groupKey: (row: Row) => string;
  /** Group keys in the order their headers print. */
  order: readonly string[];
  /** Group key → the words on its header row. */
  label: Record<string, string>;
  /** How many columns the table has — the header cell spans them all. */
  colSpan: number;
  /** Draws one row (or a row plus its fold); must carry its own `key`. */
  renderRow: (row: Row) => ReactNode;
  /** Optional second level: the inner group a row belongs to. */
  subGroupKey?: (row: Row) => string;
  /** Inner group keys in the order their headers print. */
  subOrder?: readonly string[];
  /** Inner group key → the words on its header row. */
  subLabel?: Record<string, string>;
  /** False renders the rows flat, in the order given, with no headers. */
  enabled?: boolean;
  /** An open header prints "· N" too — only where the count is what the grouping is for. */
  countOpen?: boolean;
  /** The header reads "Label (N)", open or folded, and drops the "· N hidden" caption. */
  countInLabel?: boolean;
  /** With `countInLabel`: more rows may still page in, so the count reads "N+". */
  countMore?: boolean;
  /** Group key → a class for its label's words (Families: Unassigned in the warning tone). */
  labelClassName?: Record<string, string>;
}

/** Buckets rows by key, preserving `order` first and appending unknown keys. */
function bucket<Row>(rows: Row[], key: (row: Row) => string, order: readonly string[]) {
  const buckets = new Map<string, Row[]>();
  for (const row of rows) {
    const k = key(row);
    const b = buckets.get(k);
    if (b) b.push(row);
    else buckets.set(k, [row]);
  }
  const keys = [...order.filter((k) => buckets.has(k)), ...[...buckets.keys()].filter((k) => !order.includes(k))];
  return { buckets, keys };
}

export function GroupedRows<Row>({
  rows, groupKey, order, label, colSpan, renderRow, enabled = true, subGroupKey, subOrder, subLabel, countOpen = false,
  labelClassName, countInLabel = false, countMore = false,
}: GroupedRowsProps<Row>) {
  // One set for both levels: an outer group is its own key, an inner one is
  // "<outer>|<inner>", so the same cycle under two years folds independently.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  if (!enabled) return <>{rows.map(renderRow)}</>;

  const { buckets, keys } = bucket(rows, groupKey, order);

  const toggle = (key: string) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  return (
    <>
      {keys.map((key) => {
        const rowsInGroup = buckets.get(key)!;
        const open = !collapsed.has(key);
        const base = label[key] ?? key;
        const words = countInLabel ? `${base} (${rowsInGroup.length}${countMore ? '+' : ''})` : base;
        return (
          <GroupSection
            key={key}
            words={words}
            wordsClassName={labelClassName?.[key]}
            open={open}
            count={rowsInGroup.length}
            countOpen={countOpen}
            hideCount={countInLabel}
            colSpan={colSpan}
            onToggle={() => toggle(key)}
          >
            {open && (subGroupKey
              ? renderSubGroups(rowsInGroup, key)
              : rowsInGroup.map(renderRow))}
          </GroupSection>
        );
      })}
    </>
  );

  function renderSubGroups(rowsInGroup: Row[], outerKey: string) {
    const inner = bucket(rowsInGroup, subGroupKey!, subOrder ?? []);
    return inner.keys.map((subKey) => {
      const subRows = inner.buckets.get(subKey)!;
      const composite = `${outerKey}|${subKey}`;
      const open = !collapsed.has(composite);
      return (
        <GroupSection
          key={composite}
          words={subLabel?.[subKey] ?? subKey}
          open={open}
          count={subRows.length}
          colSpan={colSpan}
          onToggle={() => toggle(composite)}
          testId="subgroup-row"
          indent
        >
          {open && subRows.map(renderRow)}
        </GroupSection>
      );
    });
  }
}

function GroupSection({
  words, wordsClassName, open, count, countOpen = false, hideCount = false, colSpan, onToggle, children, testId = 'group-row', indent = false,
}: {
  words: string; wordsClassName?: string; open: boolean; count: number; countOpen?: boolean; hideCount?: boolean; colSpan: number;
  onToggle: () => void; children: ReactNode; testId?: string; indent?: boolean;
}) {
  return (
    <>
      <tr data-testid={testId} data-group-open={open ? 'true' : 'false'} className={indent ? 'bg-muted/15' : 'bg-muted/30'}>
        <td colSpan={colSpan} className={indent ? 'pl-8 pr-3 py-1 align-middle' : 'px-3 py-1.5 align-middle'}>
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md hover:text-stevens-indigo transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stevens-indigo/40',
              indent ? 'text-caption font-medium text-stevens-ink-2' : 'text-body-sm font-medium text-foreground',
            )}
          >
            {open
              ? <ChevronDown className="h-4 w-4 text-stevens-ink-3" aria-hidden />
              : <ChevronRight className="h-4 w-4 text-stevens-ink-3" aria-hidden />}
            <span className={wordsClassName}>{words}</span>
            {!open && !hideCount && <MonoLabel className="tabular-nums">· {count} hidden</MonoLabel>}
            {open && countOpen && !hideCount && <MonoLabel className="tabular-nums">· {count}</MonoLabel>}
          </button>
        </td>
      </tr>
      {children}
    </>
  );
}
